// src/encounter/ips-med-stop-dialog.tsx
// "Stop" (discontinue) confirm for the IPS-rail Medications section (PROD-1493, the BRD's
// Edit · Stop taxonomy). MINIMAL by design — stop date only (defaults today) — NOT the Flutter
// meds-tab/Rx stop dialog (required reason, Rx-order fields), which the parent ticket explicitly
// scopes out for the rail. NO reason field: live-smoked 2026-07-07 — the PUT accepts
// `statusReason` but the record round-trip DROPS it (declared-≠-persisted), so capturing a
// clinical reason here would silently discard it. The save-path param survives for a one-line
// re-enable once the BE persists it. Destructive: on the centred modal the confirm is the DS
// destructive Button; on the anchored rail surface it is the header checkmark, labelled
// "Stop medication" (the DS glyph has no tone of its own).
import { useState } from 'react';
import { DatePicker, HelperText } from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';

export interface IpsMedStopDialogProps {
  open: boolean;
  /** Medication display name (dialog context line). */
  medication: string;
  saving?: boolean;
  onConfirm: (value: { endYyyymmdd: string; reason?: string }) => void;
  onClose: () => void;
}

const FIELD_GROUP = 'mt-3';

function toYyyymmdd(d: Date | undefined): string | null {
  if (!d) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function IpsMedStopDialog({ open, medication, saving, onConfirm, onClose }: IpsMedStopDialogProps) {
  const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());
  const [error, setError] = useState<string | null>(null);
  if (!open) return null;

  function handleConfirm() {
    const endYyyymmdd = toYyyymmdd(endDate);
    if (!endYyyymmdd) {
      setError('Pick a stop date.');
      return;
    }
    onConfirm({ endYyyymmdd });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title="Stop medication"
      size="sm"
      commit={{
        run: handleConfirm,
        label: 'Stop medication',
        text: saving ? 'Stopping…' : 'Stop medication',
        busy: saving,
        tone: 'destructive',
      }}
    >
      <div className="font-sans">
        <p className="pa-ips-paragraph m-0 text-size-sm text-text-primary">
          Stop <strong>{medication}</strong>? It moves out of the active list; the record is kept.
        </p>
        <div className={FIELD_GROUP}>
          <DatePicker label="Stop date" value={endDate} onChange={setEndDate} maxDate={new Date()} disabled={saving} />
        </div>
        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
